import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import { v4 as uuidv4 } from "uuid";

import ModalContainer from "components/ModalContainer";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import useDataSourceManager from "hooks/useDataSourceManager";
import { ReactComponent as DropdownIcon } from "assets/images/icons/icn_pagination_chevron_down.svg";
import { getLicenseTransactionTypeLabel } from "const/license";
import { getDescriptionText } from "pages/LicenseManagement/Transactions/const";

const getReportMonths = () =>
  Array.from({ length: 12 }, (_, index) =>
    dayjs()
      .subtract(index, "month")
      .startOf("month"),
  );

const escapeCsvValue = (value) =>
  `"${String(value ?? "").replace(/"/g, '""')}"`;

const downloadReport = (transactions, organization, period) => {
  const headers = [
    "Date",
    "Type",
    "Amount",
    "Description",
    "Balance After",
  ];
  const rows = (transactions || []).map((transaction) => [
    transaction.timestamp
      ? dayjs.unix(transaction.timestamp).format("YYYY-MM-DD HH:mm:ss")
      : "",
    getLicenseTransactionTypeLabel(transaction.type),
    transaction.change > 0 ? `+${transaction.change}` : (transaction.change ?? ""),
    getDescriptionText(transaction),
    transaction.balance ?? "",
  ]);
  const content = [headers, ...rows]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  const fileName = organization?.name
    ? `${organization.name}_license_report_${period}.csv`
    : `license_report_${period}.csv`;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

const ExportReportModal = ({ open, onClose, entId, organization }) => {
  const theme = useTheme();
  const { dataMap, sendRequest } = useDataSourceManager();
  const [requestId] = useState(uuidv4());
  const months = useMemo(getReportMonths, []);
  const [period, setPeriod] = useState(months[0].format("YYYY-MM"));
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open) {
      setPeriod(months[0].format("YYYY-MM"));
      setError("");
    }
  }, [open, months]);

  useEffect(() => {
    const response = dataMap[requestId];
    if (!downloading || !response) return;

    if (response.code !== undefined) {
      setError(response.message || "Unable to export the report.");
    } else {
      downloadReport(response.data, organization, period);
      onClose();
    }
    setDownloading(false);
  }, [dataMap, downloading, onClose, organization, period, requestId]);

  const handleDownload = () => {
    setError("");
    setDownloading(true);
    sendRequest(
      "/ents/:entId/orgs/:orgId/license/history/export.read",
      requestId,
      { entId, orgId: organization.id },
      { filter: { month: period } },
    );
  };

  if (!open) return null;

  return (
    <ModalContainer display={open} modalWidth='600px'>
      <Stack spacing={3}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[4],
              fontWeight: theme.typography.customFontWeight[1],
            }}
          >
            Export Report
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label='Close export report'
            sx={{ p: 0.5 }}
          >
            <CloseOutlined
              style={{
                fontSize: "18px",
                color: theme.palette.primary.greyPrimaryTransparency,
              }}
            />
          </IconButton>
        </Stack>

        <Stack spacing={1.5}>
          <Typography
            sx={{
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[1],
            }}
          >
            Period
          </Typography>
          <Select
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
            size='small'
            IconComponent={DropdownIcon}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: theme.shape.customBorderRadius[1],
                  mt: "2px",
                  boxShadow: "0px 1px 4px rgba(0,0,0,0.08)",
                  maxHeight: "240px",
                },
              },
              MenuListProps: {
                sx: {
                  "& .MuiMenuItem-root": {
                    borderRadius: theme.shape.customBorderRadius[1],
                    mx: 1,
                    my: 0.5,
                  },
                  "& .Mui-selected": {
                    backgroundColor: `${theme.palette.primary.subBlue} !important`,
                  },
                  "& .MuiMenuItem-root:hover": {
                    backgroundColor: `${theme.palette.primary.lightBlue} !important`,
                  },
                },
              },
            }}
            sx={{
              width: "310px",
              height: "31px",
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[1],
              borderRadius: theme.shape.customBorderRadius[1],
              "& .MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.mainHover,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            {months.map((month) => (
              <MenuItem
                key={month.format("YYYY-MM")}
                value={month.format("YYYY-MM")}
              >
                {month.format("MMMM YYYY")}
              </MenuItem>
            ))}
          </Select>
          {error && (
            <Typography variant='body2' color='error'>
              {error}
            </Typography>
          )}
        </Stack>

        <Stack direction='row' justifyContent='flex-end' spacing={2}>
          <OutlineButton width='120px' height='36px' onClick={onClose}>
            Close
          </OutlineButton>
          <SolidButton
            width='150px'
            height='36px'
            onClick={handleDownload}
            disabled={downloading}
          >
            Download CSV
          </SolidButton>
        </Stack>
      </Stack>
    </ModalContainer>
  );
};

export default ExportReportModal;
